import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppSelector } from '@user/store/hooks/useAppSelector'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import {
  selectMapEntityDetails,
  selectMapEntityVariant,
  selectServiceLocationId,
} from '@user/store/map/slice'
import { selectAllKnockStatuses } from '@user/store/knockStatus/slice'
import { KnockStatusHistoryRecord } from '@user/store/knockStatus/typings'
import {
  getKnockStatusHistoryByContactLocation,
  getKnockStatusHistoryByLocation,
} from '@user/store/knockStatus'
import { EntityLocationDTO } from '@user/store/map/typings'
import { hasNoBackingEntity } from './entityLocationHelpers'

const mapVariantToEntityType = (variant: string | null): string => {
  switch (variant?.toLowerCase()) {
    case 'opportunity':
      return 'Opportunity'
    case 'lead':
      return 'Lead'
    case 'pendinglead':
    case 'draftlead':
      return 'DraftLead'
    case 'knockstatus':
      return 'KnockStatus'
    default:
      return 'Unknown'
  }
}

export interface HistoryItem extends KnockStatusHistoryRecord {
  knockStatusName: string | null
  isDefault: boolean
}

export function useHistoryData(dataNewPin: EntityLocationDTO | null) {
  const dispatch = useAppDispatch()
  const entityDetails = useAppSelector(selectMapEntityDetails)
  const selectedServiceLocationId = useAppSelector(selectServiceLocationId)
  const variant = useAppSelector(selectMapEntityVariant)
  const allStatuses = useAppSelector(selectAllKnockStatuses)
  const entityType = mapVariantToEntityType(variant)

  const allStatusesRef = useRef(allStatuses)
  allStatusesRef.current = allStatuses

  const [historyList, setHistoryList] = useState<HistoryItem[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const isDraftLead = entityType !== 'Lead' && entityType !== 'Opportunity'
  const knockStatusLocationId = useMemo(() => {
    return isDraftLead
      ? entityDetails?.knockStatusDetails?.knockStatusLocationId
      : entityDetails?.locations.serviceLocations.find(
          (item) => item.id === selectedServiceLocationId,
        )?.knockStatusDetails?.knockStatusLocationId
  }, [entityDetails, isDraftLead, selectedServiceLocationId])

  const fetchHistory = useCallback(async () => {
    // SH-1781: contactLocationId can go stale/orphaned after a clear, so
    // route pins with no backing entity by knockStatusLocationId instead.
    if (
      dataNewPin &&
      dataNewPin.contactLocationId &&
      !hasNoBackingEntity(dataNewPin)
    ) {
      setIsLoading(true)
      try {
        let result: KnockStatusHistoryRecord[]

        result = await dispatch(
          getKnockStatusHistoryByContactLocation(dataNewPin.contactLocationId),
        ).unwrap()
        const enrichedList: HistoryItem[] = result.map((record) => {
          const status = allStatusesRef.current.find(
            (s) => String(s.id) === String(record.knockStatusSettingsId),
          )
          return {
            ...record,
            knockStatusName:
              status?.name ?? record.knockStatusSettingsName ?? null,
            isDefault: status?.isSystem ?? false,
          }
        })

        setHistoryList(enrichedList)
      } catch {
        setHistoryList([])
      } finally {
        setIsLoading(false)
      }
      return
    }
    // Standalone knock-status pin (entityId 0) or external PestPac pin (entityId null): both fetch
    // history by knockStatusLocationId.
    if (dataNewPin?.knockStatusLocationId && hasNoBackingEntity(dataNewPin)) {
      setIsLoading(true)
      try {
        const result = await dispatch(
          getKnockStatusHistoryByLocation(dataNewPin.knockStatusLocationId),
        ).unwrap()
        const enrichedList: HistoryItem[] = result.map((record) => {
          const status = allStatusesRef.current.find(
            (s) => String(s.id) === String(record.knockStatusSettingsId),
          )
          return {
            ...record,
            knockStatusName:
              status?.name ?? record.knockStatusSettingsName ?? null,
            isDefault: status?.isSystem ?? false,
          }
        })
        setHistoryList(enrichedList)
      } catch {
        setHistoryList([])
      } finally {
        setIsLoading(false)
      }
      return
    }

    // SH-1437: a pin with no entity of its own (a brand-new PestPac pin, entityId
    // null, or a standalone knock-status pin, entityId 0) must never fall through to
    // here — doing so picks up whatever knockStatusLocationId/variant is left in the
    // map slice from the last Lead/Opportunity/DraftLead the user viewed, showing a
    // status that doesn't belong to this pin and breaking the subsequent save.
    if (!knockStatusLocationId || !variant || hasNoBackingEntity(dataNewPin))
      return

    setIsLoading(true)
    try {
      let result: KnockStatusHistoryRecord[]

      result = await dispatch(
        getKnockStatusHistoryByLocation(knockStatusLocationId),
      ).unwrap()
      const enrichedList: HistoryItem[] = result.map((record) => {
        const status = allStatusesRef.current.find(
          (s) => String(s.id) === String(record.knockStatusSettingsId),
        )
        return {
          ...record,
          knockStatusName:
            status?.name ?? record.knockStatusSettingsName ?? null,
          isDefault: status?.isSystem ?? false,
        }
      })

      setHistoryList(enrichedList)
    } catch {
      setHistoryList([])
    } finally {
      setIsLoading(false)
    }
  }, [knockStatusLocationId, variant, dispatch, dataNewPin])

  useEffect(() => {
    setHistoryList([])
    fetchHistory()
  }, [fetchHistory])

  const reload = useCallback(() => {
    fetchHistory()
  }, [fetchHistory])

  /** The currently active knock status name (most recent active record) */
  const activeKnockStatusName =
    historyList.find((h) => h.isActive)?.knockStatusName ?? null

  const activeKnockStatusLocationId =
    historyList.find((h) => h.isActive)?.knockStatusLocationId ??
    historyList[0]?.knockStatusLocationId ??
    null

  return {
    historyList,
    isLoading,
    reload,
    activeKnockStatusName,
    activeKnockStatusLocationId,
    hasHistory: historyList.length > 0,
  }
}
